import { apiClient } from '~/api/client';
import { endpoints } from '~/api/endpoints';
import { humanizeRoleCode } from '~/utils/humanize-role';

import type {
  AddMemberPayload,
  BulkAddMembersPayload,
  InviteMemberPayload,
  PaginatedResponse,
  SearchUsersResponse,
  TenantMember,
  UpdateMemberPayload,
} from '../types/member.types';

/** Raw membership record as returned by the API */
interface ApiMembership {
  id: string;
  status: string;
  isOwner: boolean;
  joinedAt: string;
  user: {
    id: string;
    email: string;
    firstName: string | null;
    lastName: string | null;
    emailVerified: boolean;
    isSuperAdmin?: boolean;
    avatarUrl?: string | null;
  };
  role: {
    id: string;
    code: string;
    name: string;
  };
}

/** Normalize a raw API membership into the flat TenantMember shape. */
function toTenantMember(m: ApiMembership): TenantMember {
  return {
    id: m.user.id,
    email: m.user.email,
    firstName: m.user.firstName,
    lastName: m.user.lastName,
    avatarUrl: m.user.avatarUrl ?? null,
    isActive: m.status === 'ACTIVE',
    emailVerified: m.user.emailVerified,
    roleCode: m.role.code,
    // Fall back to a humanized code so a missing API name never renders as raw
    // snake_case (e.g. "subsidiary_admin" → "Subsidiary Admin"). AGENT-208.
    roleName: m.role.name || humanizeRoleCode(m.role.code),
    joinedAt: m.joinedAt,
    isSuperAdmin: m.user.isSuperAdmin ?? false,
  };
}

/**
 * List members of a tenant with pagination.
 * Handles both flat array and paginated response shapes from the API,
 * normalizing nested user/role objects into flat TenantMember records.
 */
export async function listMembers(
  tenantId: string,
  page?: number,
  limit?: number,
): Promise<PaginatedResponse<TenantMember>> {
  const res = await apiClient
    .get(endpoints.subsidiaries.members(tenantId), {
      searchParams: { page: page ?? 1, limit: limit ?? 50 },
    })
    .json<ApiMembership[] | { data: ApiMembership[]; meta?: PaginatedResponse<unknown>['meta'] }>();

  const raw = Array.isArray(res) ? res : res.data;
  const members = raw.map(toTenantMember);

  const meta =
    !Array.isArray(res) && res.meta
      ? res.meta
      : { total: members.length, page: 1, limit: members.length, totalPages: 1 };

  return { data: members, meta: meta };
}

/**
 * Search users across the platform (for the "Existing Users" tab).
 * POST /users/search with { filters: { query, excludeSubsidiaryUuid } }
 */
export async function searchUsers(
  query: string,
  excludeTenantUuid: string,
): Promise<SearchUsersResponse> {
  return apiClient
    .post(endpoints.users.search(), {
      json: {
        filters: {
          query,
          excludeSubsidiaryUuid: excludeTenantUuid,
        },
      },
    })
    .json<SearchUsersResponse>();
}

/**
 * Invite a new member to a tenant (creates user + adds to tenant).
 */
export async function inviteMember(
  tenantId: string,
  payload: InviteMemberPayload,
): Promise<unknown> {
  return apiClient.post(endpoints.subsidiaries.inviteMember(tenantId), { json: payload }).json();
}

/**
 * Add an existing user as a member of a tenant.
 */
export async function addMember(tenantId: string, payload: AddMemberPayload): Promise<unknown> {
  return apiClient.post(endpoints.subsidiaries.addMember(tenantId), { json: payload }).json();
}

/**
 * Bulk add existing users as members of a tenant.
 */
export async function bulkAddMembers(
  tenantId: string,
  payload: BulkAddMembersPayload,
): Promise<unknown> {
  return apiClient.post(endpoints.subsidiaries.bulkAddMembers(tenantId), { json: payload }).json();
}

/**
 * Update a member's role or status.
 */
export async function updateMember(
  tenantId: string,
  userId: string,
  payload: UpdateMemberPayload,
): Promise<TenantMember> {
  return apiClient
    .patch(endpoints.subsidiaries.updateMember(tenantId, userId), { json: payload })
    .json<TenantMember>();
}

/**
 * Remove a member from a tenant.
 */
export async function removeMember(tenantId: string, userId: string): Promise<unknown> {
  return apiClient.delete(endpoints.subsidiaries.removeMember(tenantId, userId)).json();
}

/**
 * Resend the invite/verification email to a member with an outstanding invite.
 * Refreshes the verification token server-side; safe to call repeatedly.
 */
export async function resendInvite(tenantId: string, userId: string): Promise<unknown> {
  return apiClient.post(endpoints.subsidiaries.resendInvite(tenantId, userId)).json();
}
